import React from 'react'
import { Card, CardContent, Typography, Box, Grid, useMediaQuery, Tooltip } from '@mui/material'
import Chart from 'react-apexcharts'
import { theme } from '../../../theme/DashboardTheme'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Grant } from '@meprism/shared/src/redux/product/ProductTypes'
import MpSvgIcon from './MpSvgIcon'
import MpTheme from '@meprism/shared/src/config/MpTheme'
import { Link } from 'react-router-dom'

interface RemovalProgressCardProps {
  removing: number
  removed: number
  grant: Grant | undefined
}

const COLORS = ['#7459d9', '#b3a5e5']

const RemovalProgressCard: React.FC<RemovalProgressCardProps> = ({ removing, removed, grant }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const totalRecords = removed + removing
  const remaining = totalRecords - removed
  const chartColors =
  removed === 0 && removing === 0
    ? ['#b3a5e5']
    : COLORS;

 const chartSeries =
  removed === 0 && removing === 0
    ? [1]
    : [removed, remaining];

  const chartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'donut',
      sparkline: { enabled: true },
    },
    labels: ['Completed', 'Remaining'],
    colors: chartColors,
    legend: { show: false },
    stroke: { width: 0, lineCap: 'round',},
    dataLabels: { enabled: false },
    tooltip: { enabled: false }, 
    states: {
      hover: {
        filter: {
          type: 'none'
        }
      },
      active: { 
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none'
        }
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: '72%',
          labels: {
            show: true,
            total: {
              show: true,
              showAlways: true,
              label: 'Total Records',
              fontSize: isMobile? '8px' : '10px',
              fontFamily: 'InterMedium',
              color: '#737373',
              formatter: () => `${totalRecords}`,
            },
            value: {
              show: true,
              fontSize: isMobile? '18px' : '20px',
              fontFamily: 'InterSemiBold',
              color: '#0a0a0a',
              offsetY: 2,
              },
          },
        },
      },
    },
  }

  return (
    <Card
      sx={{
        borderRadius: '16px',
        boxShadow: '0px 4px 10px rgba(208, 210, 218, 0.37)',
        border: grant ? 'none' : '2px solid rgba(255, 255, 255, 0.7)',
        height: 261,
        minWidth: 320,
        background: grant ? MpTheme.colors.white : 'rgba(239, 237, 253, 0.8)',
      }}
    >
      <CardContent>
        {/* Header */}
        <Box display={'flex'} justifyContent={'space-between'}
        >
        <Box display="flex" alignItems="center" gap={2.5} sx={{ mb: 10, mt: 1.5 }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: 'InterSemiBold', fontSize: 18, fontWeight: 600, color: '#000'}}
          >
            Removal Progress
          </Typography>
          {/* Tooltip wrapping the Info icon */}
          <Tooltip
            title=" This section shows how your personal data profiles are being removed from various data brokers and how many have been successfully removed"
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  fontFamily: 'InterRegular',
                  fontSize: '10px',
                  lineHeight: '16px',
                  fontWeight: 500,
                  color: '#ffffff',
                }
              },
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 18,
                color: '#D0D1D2',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </Box>
         {!grant && (
            <Box>
              <Link to='/subscription'>
                <MpSvgIcon icon="freeuserlock" />
              </Link>
            </Box>
          )}
        </Box>
        <Grid container 
                columnSpacing={2}
                alignItems="flex-start"
              >
          {/* Left Side – Chart */}
          <Grid item xs={5} md={5} display="flex" justifyContent="center">
            <Box position="relative" display="inline-flex">
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="donut"
                width={isMobile ? 130 : 150}
              />
            </Box>
          </Grid>

          {/* Right Side – Stats */}
          <Grid item xs={7} md={7} gap={4} mt={5} sx={{display: 'flex', flexDirection: 'column', justifyContent:'flex-end'}}>
            {/* Removals in Progress */}
            <Grid item xs={12}>
              <Grid container alignItems="flex-start">
                <Grid item xs={2}>
                  <Box
                    sx={{
                      width: 20,
                      height: 4,
                      mt: 1,
                      borderRadius: 3,
                      backgroundColor: '#7459D980',
                    }}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography
                    variant="body1"
                    color="#7459D9"
                    sx={{ fontFamily: 'InterRegular', fontSize: 14, fontWeight: 600 }}
                  >
                    Removals in Progress
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ fontFamily: 'InterSemiBold', fontSize: 18 }}
                  >
                    {removing}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Profiles Removed */}
            <Grid item xs={12}>
              <Grid container alignItems="flex-start">
                <Grid item xs={2}>
                  <Box
                    sx={{
                      width: 20,
                      height: 4,
                      mt: 1,
                      borderRadius: 3,
                      backgroundColor: '#7459D9',
                    }}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography
                    variant="body1"
                    color="#7459D9"
                    sx={{ fontFamily: 'InterRegular', fontSize: 14, fontWeight: 600 }}
                  >
                    Profiles Removed
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ fontFamily: 'InterSemiBold', fontSize: 18 }}
                  >
                    {removed}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default RemovalProgressCard
